const defaultTheme = require('tailwindcss/defaultTheme')

module.exports = {
  future: {
    removeDeprecatedGapUtilities: true,
  },
  purge: ['./components/**/*.tsx', './containers/**/*.tsx', './pages/**/*.tsx'],
  theme: {
    ...defaultTheme,
    extend: {
      opacity: {
        20: 0.2,
        10: 0.1,
      },
      screens: {
        xl: { min: '1280px' },
      },
      skew: {
        '9': '9deg',
        '-9': '-9deg',
      },
      borderRadius: {
        xl: '0.625rem',
        '2xl': '2rem',
      },
      margin: {
        '-80': '-20rem',
        '-28': '-7rem',
      },
      height: {
        9: '2.25rem',
        11: '2.8125rem',
        13: '3.75rem',
        80: '20rem',
        96: '24rem',
        128: '32rem',
      },
      maxHeight: {
        0: '0',
        40: '10rem',
        80: '57rem',
      },
      width: {
        80: '20rem',
        96: '24rem',
        108: '27rem',
        128: '32rem',
        148: '43rem',
        256: '64rem',
      },
      minWidth: {
        '2/6': '33.333333%',
      },
      maxWidth: {
        '2/6': '33.333333%',
        '3/5': '60%',
        '4/5': '80%',
        '48': '12rem',
      },
      borderWidth: {
        '14': '14px',
      },
      inset: {
        full: '100%',
        '1/2': '50%',
        '-5': '-5rem',
        '5': '5rem',
        '-10': '-10rem',
        '-20': '-20rem',
        '10': '10rem',
      },
      animation: {
        ping: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
        'ping-fast': 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
        'spin-slow': 'spin 3s linear infinite',
      },
      listStyleType: {
        circle: 'circle',
        'lower-latin': 'lower-latin',
      },
    },
    flexGrow: {
      default: '1',
      '0': 0,
      '1': 1,
      '2': 2,
    },
    fontSize: {
      xxs: '.625rem',
      xs: '.75rem',
      sm: '.875rem',
      tiny: '.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '2.5rem',
      '6xl': '3rem',
      '7xl': '4rem',
      '8xl': '5rem',
    },
    fontFamily: {
      sans: [
        '"Proxima Nova"',
        'system-ui',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        '"Noto Sans"',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"',
      ],
      serif: ['Georgia', 'Cambria', '"Times New Roman"', 'Times', 'serif'],
      mono: [
        'Menlo',
        'Monaco',
        'Consolas',
        '"Liberation Mono"',
        '"Courier New"',
        'monospace',
      ],
    },
    fontWeight: {
      '300': 300,
      '400': 400,
      '500': 500,
      '600': 600,
      '700': 700,
    },
    colors: {
      orange: {
        default: '#FF5A43',
        '100': '#FFF6F5',
        '200': '#FFEDEB',
        '300': '#FFBAB0',
        '400': '#FF8375',
        '500': '#FF7160',
        '600': '#FF5C4B',
        '700': '#F8402E',
        '800': '#DD3A29',
        '900': '#742c11',
      },
      orange2: {
        '50': '#FFECDC',
        '400': '#EF8F3A',
      },
      grey: {
        default: '#333333',
        '50': '#FAFAFA',
        '100': '#F3F4F7',
        '200': '#F7F8FA',
        '300': '#EBEEF2',
        '400': '#DFE3EB',
        '500': '#D8DDE6',
        '600': '#C1C6CE',
        '700': '#999999',
        '800': '#666666',
        '900': '#333333',
      },
      blue: {
        '50': '#CCEAFE',
        '100': '#C6DCFF',
        '200': '#1F95F2',
        '300': '#265397',
      },
      green: {
        '50': '#D5F0DA',
        '100': '#66B86B',
        '200': '#4AAF50',
        '300': '#438b47',
      },
      red: {
        '50': '#FAE9E6',
        '100': '#FFCBC4',
        '200': '#D41F00',
      },
      yellow: {
        '100': '#FFEED0',
        '200': '#FEBF05',
      },
      violet: {
        '50': '#F7F2FE',
        '100': '#7E36D9',
      },
      pink: {
        default: '#FFEDEB',
      },
      white: '#ffffff',
      black: '#000000',
      smoke: 'rgba(255, 255, 255, 0.75)',
      transparent: 'transparent',
      current: 'currentColor',
    },
    customForms: (theme) => ({
      default: {
        input: {
          appearance: 'none',
          color: theme('colors.grey.900'),
          backgroundColor: theme('colors.white'),
          borderColor: theme('colors.grey.500'),
          borderWidth: theme('borderWidth.default'),
          borderRadius: theme('borderRadius.default'),
          paddingTop: theme('spacing.2'),
          paddingRight: theme('spacing.4'),
          paddingBottom: theme('spacing.2'),
          minHeight: '40px',
          paddingLeft: theme('spacing.4'),
          fontSize: theme('fontSize.base'),
          lineHeight: theme('lineHeight.tight'),
          '&::placeholder': {
            color: theme('colors.grey.800'),
            opacity: '1',
          },
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
            borderColor: theme('colors.orange.default'),
          },
        },
        checkbox: {
          appearance: 'none',
          colorAdjust: 'exact',
          '&::-ms-check': {
            '@media not print': {
              color: 'transparent', // Hide the check
              background: 'inherit',
              borderColor: 'inherit',
              borderRadius: 'inherit',
            },
          },
          display: 'inline-block',
          verticalAlign: 'middle',
          backgroundOrigin: 'border-box',
          userSelect: 'none',
          flexShrink: 0,
          height: '21px',
          width: '21px',
          color: theme('colors.orange.default'),
          backgroundColor: theme('colors.white'),
          borderColor: theme('colors.grey.600'),
          borderWidth: theme('borderWidth.default'),
          borderRadius: theme('borderRadius.default'),
          iconColor: theme('colors.white'),
          icon: (iconColor) =>
            `<svg viewBox="0 0 16 16" fill="${iconColor}" xmlns="http://www.w3.org/2000/svg"><path d="M5.707 7.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4a1 1 0 0 0-1.414-1.414L7 8.586 5.707 7.293z"/></svg>`,
          '&:focus': {
            outline: 'none',
            boxShadow: 'none',
            borderColor: theme('colors.orange.500'),
          },
          '&:checked': {
            borderColor: 'transparent',
            backgroundColor: 'currentColor',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          },
        },
      },
    }),
  },
  variants: {
    borderWidth: ['responsive', 'last', 'group-hover'],
    borderColor: ['responsive', 'group-hover', 'hover', 'focus'],
    textColor: ['responsive', 'group-hover', 'hover', 'active'],
    backgroundColor: ['responsive', 'group-hover', 'hover', 'active'],
    textDecoration: ['responsive', 'group-hover', 'hover'],
  },
  plugins: [require('@tailwindcss/custom-forms')],
}
